<template>
  <div class="create-page">
    <img src="../assets/images/hero/flight-layers/bg_mailbox.webp" alt="" class="create-page__bg" aria-hidden="true" fetchpriority="high">

    <div class="create-page__header">
      <h1 class="create-page__title">Create a Postcard</h1>
      <p class="create-page__subtitle">
        This isn't about crafting the perfect message — it's about sending a perfectly unique little surprise.
        You choose the vibe. We deliver the magic.
      </p>
    </div>

    <div class="card">
      <form @submit.prevent="handleAddToCart">

        <!-- Section: Who's it for? -->
        <fieldset class="form-section">
          <legend class="form-section__legend">Who's it for?</legend>

          <div class="form-group" v-if="peopleOptions.length > 0">
            <label>
              Send to one of your people?
              <select v-model="selectedPersonId" @change="onPersonSelected">
                <option value="">-- Choose manually --</option>
                <option v-for="p in peopleOptions" :key="p.id" :value="p.id">
                  {{ p.name }}{{ p.hasAddress ? '' : ' (no address)' }}
                </option>
              </select>
            </label>
          </div>

          <div class="form-group">
            <label>
              What kind of relationship is this?
              <select v-model="postcard.category">
                <option disabled value="">Select a relationship</option>
                <option value="Family">Family</option>
                <option value="Friends">Friend</option>
                <option value="Romantic">Romantic Relationship</option>
                <option value="Other">Professional / Misc</option>
              </select>
            </label>
          </div>

          <div class="form-group">
            <label v-if="postcard.category !== 'Other'">
              Recipient:
              <select v-model="postcard.recipient">
                <option disabled value="">Select a recipient</option>
                <option v-for="recipient in recipients[postcard.category]" :key="recipient" :value="recipient">
                  {{ recipient }}
                </option>
              </select>
            </label>
          </div>
        </fieldset>

        <!-- Section: What's the vibe? -->
        <fieldset class="form-section">
          <legend class="form-section__legend">What's the vibe?</legend>

        <div class="form-group">
          <label>
            What kind of vibe are you going for?
            <select v-model="postcard.tone">
              <option disabled value="">Select a vibe</option>
              <option v-for="tone in tones[postcard.category]" :key="tone" :value="tone">
                {{ tone }}
              </option>
              <option value="Surprise me">Surprise me</option>
            </select>
          </label>
        </div>

        <div class="form-group">
          <!-- Occasion (Optional) -->
          <label>
            Occasion (optional):
            <select v-model="postcard.occasion">
              <option disabled value="">Select an occasion</option>
              <option value="Thinking of U">Thinking of U</option>
              <option value="Just Because">Just Because</option>
              <option value="Birthday">Birthday</option>
              <option value="Anniversary">Anniversary</option>
              <option value="Congratulations">Congratulations</option>
              <option value="Apology">Apology</option>
              <option value="Sympathy">Sympathy</option>
              <option value="Other">Other</option>
            </select>
          </label>

          <div v-if="postcard.occasion === 'Other'">
            <label for="customOccasion">Enter Your Custom Occasion:</label>
            <input
                type="text"
                id="customOccasion"
                v-model="postcard.customOccasion"
                maxlength="80"
                placeholder="Enter your occasion."
                class="form-input"
            />
          </div>
        </div>

        <div class="form-group">
          <!-- Message Style -->
          <label>
            Message Style:
            <select v-model="postcard.style">
              <option disabled value="">Select a style</option>
              <option v-for="style in styles" :key="style" :value="style">
                {{ style }}
              </option>
            </select>
          </label>

          <!-- Substyles -->
          <div v-if="postcard.style === 'Poem'">
            <label>
              Poem Style:
              <select v-model="postcard.poemStyle">
                <option v-for="substyle in substyles.Poem" :key="substyle" :value="substyle">
                  {{ substyle }}
                </option>
              </select>
            </label>
          </div>

          <div v-if="postcard.style === 'Song lyrics'">
            <label>
              Genre:
              <select v-model="postcard.songLyrics">
                <option v-for="substyle in substyles.SongLyrics" :key="substyle" :value="substyle">
                  {{ substyle }}
                </option>
              </select>
            </label>
          </div>

          <div v-if="postcard.style === 'Story'">
            <label>
              Story Type:
              <select v-model="postcard.story">
                <option v-for="substyle in substyles.Story" :key="substyle" :value="substyle">
                  {{ substyle }}
                </option>
              </select>
            </label>
          </div>

          <div v-if="postcard.style === 'Joke'">
            <label>
              Joke Type:
              <select v-model="postcard.joke">
                <option v-for="substyle in substyles.Joke" :key="substyle" :value="substyle">
                  {{ substyle }}
                </option>
              </select>
            </label>
          </div>

          <div v-if="postcard.style === 'Affirmation'">
            <label>
              Encouragement & Affirmation Type:
              <select v-model="postcard.affirmation">
                <option v-for="substyle in substyles.Affirmation" :key="substyle" :value="substyle">
                  {{ substyle }}
                </option>
              </select>
            </label>
          </div>

          <div v-if="postcard.style === 'Note'">
            <label>
              Note Type:
              <select v-model="postcard.note">
                <option v-for="substyle in substyles.Notes" :key="substyle" :value="substyle">
                  {{ substyle }}
                </option>
              </select>
            </label>
          </div>

          <!-- Custom Style Field -->
          <div
              v-if="
            postcard.joke === 'Other' ||
            postcard.poemStyle === 'Other' ||
            postcard.songLyrics === 'Other' ||
            postcard.story === 'Other' ||
            postcard.affirmation === 'Other'
          "
          >
            <label for="customStyle">Enter Your Custom Style:</label>
            <input
                type="text"
                id="customStyle"
                v-model="postcard.customStyle"
                maxlength="80"
                placeholder="Enter your style"
                class="form-input"
            />
          </div>
        </div>

        </fieldset>

        <!-- Section: The details -->
        <fieldset class="form-section">
          <legend class="form-section__legend">The details</legend>

        <div class="form-group">
          <label for="userIntent">Want to add a hint or note? (optional)</label>
          <input
              type="text"
              id="userIntent"
              v-model="postcard.userIntent"
              maxlength="240"
              placeholder="e.g. She's had a hard week, or: He loves raccoons and tacos"
              class="form-input"
          />
        </div>

        <div class="form-group">
          <!-- From Field -->
          <label for="fromField">Who is this from?</label>
          <input
              type="text"
              id="fromField"
              v-model="postcard.fromField"
              required
              class="form-input"
          />
        </div>

        <div class="form-group">
          <label>
            Recipient's Pronouns (optional):
            <select v-model="postcard.recipientPronouns">
              <option value=""></option>
              <option value="they/them">They / Them</option>
              <option value="she/her">She / Her</option>
              <option value="he/him">He / Him</option>
              <option value="custom">Custom</option>
            </select>
          </label>
        </div>

        <input
            v-if="postcard.recipientPronouns === 'custom'"
            v-model="postcard.customPronouns"
            placeholder="e.g. Ze/Hir, Xe/Xem"
            class="form-input"
        />

        <div class="form-group">
          <!-- Recipient Name -->
          <label for="recipientFirstName">Recipient First Name:</label>
          <input
              type="text"
              id="recipientFirstName"
              v-model="postcard.recipientFirstName"
              required
              class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="recipientLastName">Recipient Last Name:</label>
          <input
              type="text"
              id="recipientLastName"
              v-model="postcard.recipientLastName"
              required
              class="form-input"
          />
        </div>

        </fieldset>

        <!-- Section: Where to send it? -->
        <fieldset class="form-section">
          <legend class="form-section__legend">Where to send it?</legend>

        <div class="form-group">
          <label>Recipient Address</label>
          <SimpleGoogleAutocomplete
              ref="addressInput"
              @update:modelValue="handleAddressUpdate"
          />

          <p v-if="addressIsMissing && postcard.address.street"
              class="error-message">
            Please select a full address from the dropdown list.
          </p>

          <input
              v-model="postcard.address.optional"
              class="form-input"
              type="text"
              placeholder="Apt, Unit, Suite (optional)"
          />
        </div>

        </fieldset>

        <!-- No Preview Notice -->
        <div class="no-preview-notice">
          <span class="notice-text">No preview — just surprises</span>
          <button type="button" class="info-trigger" @click="showPhilosophy = !showPhilosophy" aria-label="Why no preview?">
            <font-awesome-icon :icon="['fas', 'circle-info']" />
          </button>
          <transition name="fade">
            <div v-if="showPhilosophy" class="philosophy-tooltip">
              <p><strong>Why no preview?</strong></p>
              <p>
                Thinking of U is about the <em>gesture</em>, not perfection. When you obsess over every word,
                you lose the spontaneity that makes a surprise special.
              </p>
              <p>
                We let you set the vibe, then trust the magic. Not happy with what arrived?
                We'll send another one free. But honestly? The "imperfect" ones often land the hardest.
              </p>
              <p class="philosophy-tagline">Send the vibe. Skip the overthinking.</p>
            </div>
          </transition>
        </div>

        <div class="button-wrapper">
          <button
              class="cta-button submit-btn"
              type="submit"
              :disabled="isSubmitting || addressIsMissing"
          >
            <span>Add to Cart</span>
          </button>
        </div>
      </form>
    </div>

    <!-- Spinner and Modal -->
    <Spinner :show="loading"/>
    <transition name="modal-fade">
      <div v-if="showModal" class="modal" @click.self="closeModal">
        <div class="modal-content">
          <div v-if="modalMessage === 'Success!'" class="modal-icon modal-icon--success">
            <font-awesome-icon :icon="['fas', 'check']" />
          </div>
          <p>{{ modalMessage }}</p>
          <button @click="closeModal" class="modal-btn">Close</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {validateAddress, moderateUserInput} from '@/api';
import Spinner from "@/components/SpinnerComp.vue";
import { AddressAutocomplete as SimpleGoogleAutocomplete } from '@thinking-of-u/ui';
import { createEmptyPostcard, recipients, tones, styles, substyles } from '../data/postcardOptions.js';
import { db, auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";

export default {
  name: 'PostcardCreation',
  components: {
    Spinner,
    SimpleGoogleAutocomplete,
  },
  data() {
    return {
      testAddress: {},
      loading: false,
      showModal: false,
      showPhilosophy: false,
      modalMessage: '',
      postcard: createEmptyPostcard(),
      recipients,
      tones,
      styles,
      substyles,
      selectedPersonId: '',
      authUnsubscribe: null,
    };
  },
  computed: {
    ...mapGetters(['getPostcard', 'relationships']),
    addressIsMissing() {
      return !this.isAddressComplete(this.postcard.address);
    },
    peopleOptions() {
      return (this.relationships || []).filter((r) => r.status === 'active');
    },
  },
  mounted() {
    // Load any stored postcard data from Vuex
    if (this.getPostcard && Object.keys(this.getPostcard).length) {
      this.postcard = structuredClone(this.getPostcard);
    }
    // Fetch relationships if not already loaded
    if (!this.relationships || this.relationships.length === 0) {
      this.fetchRelationships();
    }
  },
  beforeUnmount() {
    // Tear down any auth listener registered in fetchRelationships so it does
    // not fire callbacks against this destroyed instance after navigation.
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = null;
    }
  },
  watch: {
    'postcard.style'(newStyle) {
      if (newStyle !== 'Poem') this.postcard.poemStyle = '';
      if (newStyle !== 'Song lyrics') this.postcard.songLyrics = '';
      if (newStyle !== 'Story') this.postcard.story = '';
      if (newStyle !== 'Joke') this.postcard.joke = '';
      if (newStyle !== 'Affirmation') this.postcard.affirmation = '';
      if (newStyle !== 'Note') this.postcard.note = '';

      // Also clear customStyle if none of the substyles are "Other"
      this.postcard.customStyle = '';
    },
  },
  methods: {
    ...mapActions(['clearPostcard', 'addToCart', 'setRelationships']),
    async fetchRelationships() {
      const user = auth.currentUser;
      if (!user) {
        // Wait for auth state. Store the unsubscribe so it is torn down on
        // unmount (beforeUnmount) and after the first authenticated callback,
        // preventing a listener leak across remounts of this route.
        this.authUnsubscribe = onAuthStateChanged(auth, async (u) => {
          if (u) {
            if (this.authUnsubscribe) {
              this.authUnsubscribe();
              this.authUnsubscribe = null;
            }
            await this.loadRelationships(u.uid);
          }
        });
        return;
      }
      await this.loadRelationships(user.uid);
    },
    async loadRelationships(uid) {
      try {
        const ref = collection(db, "relationships");
        const q = query(ref, where("userId", "==", uid), where("status", "==", "active"));
        const snapshot = await getDocs(q);
        const results = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        this.setRelationships(results);
      } catch {
        // Non-blocking — picker just won't show
      }
    },
    mapTypeToCategory(type) {
      const map = { friend: "Friends", family: "Family", romantic: "Romantic", partner: "Romantic" };
      return map[type] || "Other";
    },
    onPersonSelected() {
      if (!this.selectedPersonId) {
        // Clear — reset fields
        this.postcard.recipientFirstName = '';
        this.postcard.recipientLastName = '';
        this.postcard.category = '';
        this.postcard.recipient = '';
        this.postcard.address = {
          street: '', optional: '', city: '', state: '', postalCode: '', country: '', formattedAddress: '', location: null
        };
        this.$refs.addressInput?.reset?.();
        return;
      }
      const person = this.peopleOptions.find((p) => p.id === this.selectedPersonId);
      if (!person) return;

      const nameParts = person.name.split(" ");
      this.postcard.recipientFirstName = nameParts[0] || '';
      this.postcard.recipientLastName = nameParts.slice(1).join(" ") || '';
      this.postcard.category = this.mapTypeToCategory(person.type);
      this.postcard.recipient = person.type || '';

      if (person.hasAddress && person.address) {
        this.postcard.address = {
          street: person.address.street || '',
          optional: person.address.optional || '',
          city: person.address.city || '',
          state: person.address.state || '',
          postalCode: person.address.postalCode || '',
          country: person.address.country || '',
          formattedAddress: person.address.formattedAddress || '',
          location: person.address.location || null,
        };
        // Display the address in the autocomplete input
        const displayAddr = person.address.formattedAddress ||
          [person.address.street, person.address.city, person.address.state, person.address.postalCode, person.address.country].filter(Boolean).join(", ");
        this.$refs.addressInput?.setQuery?.(displayAddr);
      }
    },
    handleAddressUpdate(parsedAddress) {
      if (!parsedAddress) {
        this.postcard.address = {
          street: '',
          optional: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          formattedAddress: '',
          location: null
        };
        return;
      }

      this.postcard.address = {
        ...this.postcard.address,
        ...parsedAddress
      };
    },
    async handleAddToCart() {
      try {
        this.loading = true;
        console.log('📦 Full Postcard JSON:', JSON.stringify(this.postcard, null, 2));

        console.log('📦 Address JSON:', JSON.stringify(this.postcard.address, null, 2));

        const safe = await this.moderateCustomInputs();
        if (!safe) return;

        if (!this.isAddressComplete(this.postcard.address)) {
          this.modalMessage = 'Please select a full address from the dropdown before continuing.';
          this.showModal = true;
          this.loading = false;
          return;
        }
        const {data} = await validateAddress({address: this.postcard.address});

        if (!data.isValid) {
          this.modalMessage = 'Error: invalid Address';
          this.showModal = true;
          return;
        }

        // Add to cart, then clear the in-progress postcard from sessionStorage
        this.addToCart(this.postcard);
        this.clearPostcard();

        this.resetForm();

        this.modalMessage = `Success!`;
        this.showModal = true;
      } catch (error) {
        this.modalMessage = `Error adding to cart, please try again. If issue persists please contact support.`;
        this.showModal = true;
      } finally {
        this.loading = false;
      }
    },
    async moderateCustomInputs() {
      const inputsToCheck = [
        {label: 'custom style', value: this.postcard.customStyle},
        {label: 'custom occasion', value: this.postcard.customOccasion},
        {label: 'custom pronouns', value: this.postcard.customPronouns},
        {label: 'user intent', value: this.postcard.userIntent}
      ];

      for (const input of inputsToCheck) {
        if (input.value) {
          const {data: moderationResult} = await moderateUserInput({input: input.value});

          if (!moderationResult.isAllowed) {
            this.modalMessage = `Your ${input.label} contains inappropriate content. Please revise it.`;
            this.showModal = true;
            if (input.label === 'custom style') this.postcard.customStyle = '';
            if (input.label === 'custom occasion') this.postcard.customOccasion = '';
            if (input.label === 'custom pronouns') this.postcard.customPronouns = '';
            if (input.label === 'user intent') this.postcard.userIntent = '';
            return false;
          }
        }
      }

      return true;
    },
    isAddressComplete(address) {
      return (
          address &&
          typeof address === 'object' &&
          address.street &&
          address.city &&
          address.state &&
          address.postalCode &&
          address.country
      );
    },
    closeModal() {
      this.showModal = false;
    },
    resetForm() {
      this.postcard = {
        category: '',
        recipient: '',
        recipientPronouns: '',
        tone: '',
        occasion: '',
        style: '',
        userIntent: '',
        poemStyle: '',
        songLyrics: '',
        affirmation: '',
        joke: '',
        story: '',
        note: '',
        recipientFirstName: '',
        recipientLastName: '',
        fromField: '',
        customStyle: '',
        customOccasion: '',
        customPronouns: '',
        address: {
          street: '',
          optional: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        },
        message: '',
        imageFromDALLEUrl: ''
      };

      this.selectedPersonId = '';
      this.$refs.addressInput?.reset?.();
    }
  }
};
</script>

<style scoped>
/* === Page layout === */
.create-page {
  position: relative;
  min-height: 100vh;
  padding: 0 1rem 3rem;
  overflow-x: hidden;
}

.create-page__bg {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.12;
  pointer-events: none;
  z-index: 0;
}

/* === Header === */
.create-page__header {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 3rem 1rem 1.5rem;
  max-width: 600px;
  margin: 0 auto;
}

.create-page__title {
  font-size: 2.4rem;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.75rem;
}

.create-page__subtitle {
  font-size: 1.05rem;
  font-style: italic;
  color: #555;
  line-height: 1.7;
}

/* === Card === */
.card {
  position: relative;
  z-index: 1;
  max-width: 680px;
  margin: 0 auto;
  padding: 2rem 2.5rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  animation: cardIn 0.6s ease both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* === Form sections === */
.form-section {
  border: none;
  padding: 0 0 1.5rem;
  margin: 0 0 1.5rem;
  border-bottom: 1px solid rgba(228, 19, 147, 0.12);
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.form-section__legend {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--violet-lush);
  margin-bottom: 1rem;
  padding: 0;
}

/* === Form elements === */
.form-group {
  margin-bottom: 1.25rem;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  font-size: 0.92rem;
  color: var(--violet-lush);
}

select,
input,
textarea {
  display: block;
  margin-top: 5px;
  padding: 10px 14px;
  border: 1.5px solid #ddd;
  background-color: white;
  border-radius: 10px;
  font-size: 0.95rem;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

select:focus,
input:focus,
textarea:focus {
  outline: none;
  border-color: var(--hollywood-cerise);
  box-shadow: 0 0 0 3px rgba(228, 19, 147, 0.1);
}

select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2359417f' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
}

.error-message {
  color: #e53e3e;
  font-size: 0.85rem;
  margin-top: 6px;
}

/* === Submit button === */
.button-wrapper {
  text-align: center;
  margin-top: 1.5rem;
}

.submit-btn {
  padding: 14px 40px;
  font-size: 1.05rem;
  font-weight: 600;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  color: white;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 16px rgba(228, 19, 147, 0.3);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(228, 19, 147, 0.4);
}

.submit-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* === Modal === */
.modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.modal-content {
  background: white;
  padding: 2rem 2.5rem;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  margin: 0 1rem;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  animation: modalIn 0.3s ease both;
}

@keyframes modalIn {
  from { opacity: 0; transform: scale(0.9) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.modal-icon--success {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: var(--hollywood-cerise);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin: 0 auto 1rem;
  animation: checkBounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes checkBounce {
  from { transform: scale(0); }
  to { transform: scale(1); }
}

.modal-content p {
  color: var(--rich-black);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 1.25rem;
}

.modal-btn {
  padding: 10px 28px;
  border: none;
  border-radius: 20px;
  background: var(--hollywood-cerise);
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.modal-btn:hover {
  background: var(--violet-lush);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* === No Preview Notice === */
.no-preview-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1.5rem 0 0.5rem;
  position: relative;
  flex-wrap: wrap;
}

.notice-text {
  font-size: 0.95rem;
  color: var(--violet-lush);
  font-weight: 500;
}

.info-trigger {
  background: none;
  border: none;
  color: var(--hollywood-cerise);
  cursor: pointer;
  padding: 0.25rem;
  font-size: 1.1rem;
  transition: transform 0.2s, color 0.2s;
}

.info-trigger:hover {
  transform: scale(1.15);
  color: var(--violet-lush);
}

.philosophy-tooltip {
  width: 100%;
  margin-top: 1rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, var(--powder-blue) 0%, #f8f4ff 100%);
  border-radius: 12px;
  border-left: 4px solid var(--hollywood-cerise);
  text-align: left;
}

.philosophy-tooltip p {
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  color: #444;
  line-height: 1.5;
}

.philosophy-tooltip p:last-child {
  margin-bottom: 0;
}

.philosophy-tooltip em {
  color: var(--hollywood-cerise);
  font-style: italic;
}

.philosophy-tagline {
  font-weight: 600;
  color: var(--violet-lush) !important;
  font-style: italic;
  margin-top: 0.5rem !important;
}

/* === Transitions === */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* === Mobile === */
@media (max-width: 767px) {
  .create-page__title {
    font-size: 1.8rem;
  }

  .card {
    padding: 1.5rem 1.25rem;
    border-radius: 16px;
  }

  select,
  input,
  textarea {
    font-size: 16px;
    padding: 12px 14px;
  }
}

/* === Reduced motion === */
@media (prefers-reduced-motion: reduce) {
  .card,
  .modal-content,
  .modal-icon--success {
    animation: none;
  }
}
</style>
